/* eslint-disable react/jsx-no-constructed-context-values */
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Modal } from 'antd'
import PropTypes from 'prop-types'

import AcessoContext from 'Components/Acesso/Context'

import api from 'Services/api'
import acessoApiService from 'Services/api/acesso'
import { SmileOutlined } from '@ant-design/icons'
import controleAcessoApiService from 'Services/api/controleAcesso'

const propTypes = {
  children: PropTypes.element.isRequired
}

const isTokenAcessoSalvo = () => {
  const accessToken = window.localStorage.getItem('earetaguarda_access_token')
  return !!accessToken
}

const AcessoProvider = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [isAuthenticated, setIsAuthenticated] = useState(isTokenAcessoSalvo)
  const [controleAcesso, setControleAcesso] = useState([])

  const login = (credentials) => {
    const authenticateUser = () => {
      setIsAuthenticated(true)
    }

    const redirectToPreviousLocationOrIndex = () => {
      const from = location.state?.from?.pathname || '/'
      navigate(from, { replace: true })
    }

    const saveTokensAndUpdateApiAuthorizationHeader = (tokens) => {
      window.localStorage.setItem('earetaguarda_access_token', tokens.access)
      window.localStorage.setItem('earetaguarda_refresh_token', tokens.refresh)

      api.updateAuthorizationHeader(tokens.access)
    }

    const exibirMensagemDeBoasVindas = () => {
      const usuarioNome = window.localStorage.getItem('usuario_nome')
      const mensagem = `Seja bem-vindo ao novo EasyAssist Web, ${usuarioNome}! Fique à vontade para explorar todas as funcionalidades que oferecemos. Descubra como tornamos a sua experiência ainda melhor.`
      const titulo = 'Novo EasyAssist Web'
      const icone = <SmileOutlined />
      const iconeStyle = { color: '#1890ff', fontSize: '24px' }
      const style = {
        content: {
          fontSize: '16px',
          lineHeight: '24px'
        }
      }
      const shouldShow =
        window.localStorage.getItem('showWelcomeMessage') !== 'v1'

      if (shouldShow) {
        Modal.info({
          content: mensagem,
          okButtonProps: { type: null },
          title: titulo,
          icon: icone,
          iconStyle: iconeStyle,
          width: 480,
          styles: style,
          onOk: () => {
            window.localStorage.setItem('showWelcomeMessage', 'v1')
          }
        })
      }
    }

    return acessoApiService
      .login(credentials)
      .then((response) => response.data)
      .then(saveTokensAndUpdateApiAuthorizationHeader)
      .then(authenticateUser)
      .then(redirectToPreviousLocationOrIndex)
      .then(exibirMensagemDeBoasVindas)
  }

  /**
   * Checa se o usuário tem acesso a uma ou mais operações em um sistema
   *
   * @param {number|number[]} idOperacaoOuIdOperacoes - Um id de operação ou um array de ids de operações
   * @param {number|null} [idEstabelecimento=null] - O id do estabelecimento a ser verificado caso especificado
   * @returns {boolean} - True se o usuário tem acesso, false caso contrário
   */
  const checkControleAcesso = (
    idOperacaoOuIdOperacoes,
    idEstabelecimento = null
  ) => {
    // se controleAcesso não estiver carregado, retorna false
    if (controleAcesso.length === 0) {
      return false
    }

    let isOperacaoInControleAcesso = false
    if (Array.isArray(idOperacaoOuIdOperacoes)) {
      isOperacaoInControleAcesso = idOperacaoOuIdOperacoes.some(
        (operacao) => operacao in controleAcesso
      )
    } else {
      isOperacaoInControleAcesso = idOperacaoOuIdOperacoes in controleAcesso
    }

    if (!isOperacaoInControleAcesso) {
      return false
    }

    if (!idEstabelecimento) {
      return true
    }

    const operacaoSelecionada = Array.isArray(idOperacaoOuIdOperacoes)
      ? idOperacaoOuIdOperacoes.find((op) => op in controleAcesso)
      : idOperacaoOuIdOperacoes

    return idEstabelecimento in controleAcesso[operacaoSelecionada]
  }

  const logout = () => {
    const removeTokens = () => {
      window.localStorage.removeItem('earetaguarda_access_token')
      window.localStorage.removeItem('earetaguarda_refresh_token')
    }

    removeTokens()
    setIsAuthenticated(false)
    window.location.href = '/login'
  }

  const fetchControleAcesso = () => {
    controleAcessoApiService
      .me()
      .then((response) => response.data)
      .then((data) => {
        setControleAcesso(data)
      })
      .catch((error) => {
        console.error('Erro ao buscar controle de acesso:', error)
      })
  }

  useEffect(() => {
    if (isAuthenticated) {
      if (controleAcesso.length === 0) {
        fetchControleAcesso()
      }
    }
  }, [isAuthenticated])

  return (
    <AcessoContext.Provider
      value={{ isAuthenticated, login, logout, checkControleAcesso }}
    >
      {children}
    </AcessoContext.Provider>
  )
}

AcessoProvider.propTypes = propTypes

export default AcessoProvider
